/** Purely local UI state: open dialogs and screens the player opened on purpose. */
import { batch, type ReadonlySignal, type Signal, signal } from '@preact/signals';

/** race: the race's settings (the host changes them between races, everybody can look). */
export type DialogId = 'settings' | 'rooms' | 'invite' | 'installGuide' | 'race';

/** first-run: right after the first login (no way back, only "Готово"); edit: from the room. */
export type ProfileEditorMode = 'first-run' | 'edit';

export class UiStore {
  private readonly dialogSignal: Signal<DialogId | null> = signal(null);
  private readonly profileEditorSignal: Signal<ProfileEditorMode | null> = signal(null);
  private readonly watchingSignal: Signal<string | null> = signal(null);
  private readonly dismissedResultsSignal: Signal<string | null> = signal(null);
  private readonly loginNoticeSignal: Signal<string | null> = signal(null);
  private readonly statsOpenSignal: Signal<boolean> = signal(false);

  readonly dialog: ReadonlySignal<DialogId | null> = this.dialogSignal;
  readonly profileEditor: ReadonlySignal<ProfileEditorMode | null> = this.profileEditorSignal;
  /** Race id the player chose to watch as a spectator. */
  readonly watchingRaceId: ReadonlySignal<string | null> = this.watchingSignal;
  /** Race id whose results the player closed. */
  readonly dismissedResultsId: ReadonlySignal<string | null> = this.dismissedResultsSignal;
  /** Message for the login screen (e.g. "log in again"). */
  readonly loginNotice: ReadonlySignal<string | null> = this.loginNoticeSignal;
  /** The statistics screen is open (from the room's tools). */
  readonly statsOpen: ReadonlySignal<boolean> = this.statsOpenSignal;

  openDialog(dialog: DialogId): void {
    this.dialogSignal.value = dialog;
  }

  closeDialog(): void {
    this.dialogSignal.value = null;
  }

  openProfileEditor(mode: ProfileEditorMode): void {
    batch(() => {
      this.dialogSignal.value = null;
      this.profileEditorSignal.value = mode;
    });
  }

  closeProfileEditor(): void {
    this.profileEditorSignal.value = null;
  }

  openStats(): void {
    batch(() => {
      this.dialogSignal.value = null;
      this.statsOpenSignal.value = true;
    });
  }

  closeStats(): void {
    this.statsOpenSignal.value = false;
  }

  watchRace(raceId: string | null): void {
    this.watchingSignal.value = raceId;
  }

  dismissResults(raceId: string): void {
    this.dismissedResultsSignal.value = raceId;
  }

  /** Brings back results the player closed earlier. */
  showResults(): void {
    this.dismissedResultsSignal.value = null;
  }

  setLoginNotice(notice: string | null): void {
    this.loginNoticeSignal.value = notice;
  }

  /** Back to a clean state (logout). */
  reset(): void {
    batch(() => {
      this.dialogSignal.value = null;
      this.profileEditorSignal.value = null;
      this.watchingSignal.value = null;
      this.dismissedResultsSignal.value = null;
      this.statsOpenSignal.value = false;
    });
  }
}
